import { range } from 'lodash';
import { BaseSliderFilterModel } from '../../../components/filters/base-slider-filter.component';
import { EnhancedRating } from '../../../../helpers/issue/ratings';

export const RATINGS = [
  'AAA',
  'AA+',
  'AA',
  'AA-',
  'A+',
  'A',
  'A-',
  'BBB+',
  'BBB',
  'BBB-',
  'Non-IG'
];

export const ST_RATINGS = [
  {
    equivRating: 'AAA',
    vals: [
      'MIG 1',
      'MIG1',
      'VMIG 1',
      'VMIG1',
      'P1',
      'PRIME-1',
      'A-1+',
      'SP1+',
      'F1+',
      'K1+'
    ]
  },
  {
    equivRating: 'AA+',
    vals: [
      'MIG 1',
      'MIG1',
      'VMIG 1',
      'VMIG1',
      'P1',
      'PRIME-1',
      'A-1+',
      'SP-1+',
      'F1+',
      'K1+'
    ]
  },
  {
    equivRating: 'AA',
    vals: [
      'MIG 1',
      'MIG1',
      'VMIG 1',
      'VMIG1',
      'P1',
      'PRIME-1',
      'A-1+',
      'SP-1+',
      'F1+',
      'K1+'
    ]
  },
  {
    equivRating: 'AA-',
    vals: [
      'MIG 1',
      'MIG1',
      'VMIG 1',
      'VMIG1',
      'P1',
      'PRIME-1',
      'A-1+',
      'SP-1+',
      'F1+',
      'K1+'
    ]
  },
  {
    equivRating: 'A+',
    vals: [
      'MIG 1',
      'MIG1',
      'VMIG 1',
      'VMIG1',
      'P1',
      'PRIME-1',
      'A-1',
      'SP-1',
      'F1',
      'K1+',
      'K1'
    ]
  },
  {
    equivRating: 'A',
    vals: [
      'MIG 1',
      'MIG1',
      'VMIG 1',
      'VMIG1',
      'P1',
      'PRIME-1',
      'A-1',
      'SP-1',
      'F1',
      'K1'
    ]
  },
  {
    equivRating: 'A-',
    vals: [
      'MIG 2',
      'MIG2',
      'VMIG 2',
      'VMIG2',
      'P2',
      'PRIME-2',
      'A-2',
      'SP-2',
      'F2',
      'K1',
      'K2'
    ]
  },
  {
    equivRating: 'BBB+',
    vals: [
      'MIG 2',
      'MIG2',
      'VMIG 2',
      'VMIG2',
      'P2',
      'PRIME-2',
      'A-2',
      'SP-2',
      'F2',
      'K2'
    ]
  },
  {
    equivRating: 'BBB',
    vals: [
      'MIG 3',
      'MIG3',
      'VMIG 3',
      'VMIG3',
      'P3',
      'PRIME-3',
      'A-3',
      'SP-3',
      'F3',
      'K2',
      'K3'
    ]
  },
  {
    equivRating: 'BBB-',
    vals: [
      'MIG 3',
      'MIG3',
      'VMIG 3',
      'VMIG3',
      'P3',
      'PRIME-3',
      'A-3',
      'SP-3',
      'F3',
      'K3'
    ]
  },
  {
    equivRating: 'Non-IG',
    vals: ['NR', 'NOTPRIME', 'B', 'C', 'D', 'SD', 'WD']
  }
];

export const AAA_BUCKET_INDEX = RATINGS.indexOf('AAA');
export const BBB_MINUS_BUCKET_INDEX = RATINGS.indexOf('BBB-');
export const NON_IG_BUCKET_INDEX = RATINGS.indexOf('Non-IG');

export enum RatingsFilterValue {
  all = 'all',
  rated = 'rated',
  notRated = 'notRated'
}

export enum PredefinedEnhancementFilterValue {
  all = 'all',
  enhanced = 'enhanced',
  unenhanced = 'unenhanced'
}

export const RATINGS_FILTER_OPTIONS = [
  {
    name: RatingsFilterValue.all,
    label: 'All'
  },
  {
    name: RatingsFilterValue.rated,
    label: 'Rated'
  },
  {
    name: RatingsFilterValue.notRated,
    label: 'Not Rated'
  }
];

export const ENHANCEMENT_FILTER_OPTIONS = [
  {
    name: PredefinedEnhancementFilterValue.all,
    label: 'Unfiltered'
  },
  {
    name: PredefinedEnhancementFilterValue.enhanced,
    label: 'Enhanced Only'
  },
  {
    name: PredefinedEnhancementFilterValue.unenhanced,
    label: 'Unenhanced Only'
  }
];

export function getRatingFilterOptionLabel(name: RatingsFilterValue): string {
  const labelItms = RATINGS_FILTER_OPTIONS.filter((i) => i.name === name);
  if (labelItms && labelItms.length === 1) {
    return labelItms[0].label;
  }
  return '';
}

export function getEnhancementFilterOptionLabel(
  name: PredefinedEnhancementFilterValue
): string {
  const labelItms = ENHANCEMENT_FILTER_OPTIONS.filter((i) => i.name === name);
  if (labelItms && labelItms.length === 1) {
    return labelItms[0].label;
  }
  return '';
}

export interface RatingEnhancementFilterModel extends BaseSliderFilterModel {
  radio: string;
  ratingsRadio?: string;
  enhancements?: string[];
}

export function ratingPassesFilter(
  ratingData: EnhancedRating,
  minValue: number,
  maxValue: number
): boolean {
  //When filtering Non-IG only, include BBB- rated deals
  if (minValue === NON_IG_BUCKET_INDEX && maxValue === NON_IG_BUCKET_INDEX) {
    minValue = BBB_MINUS_BUCKET_INDEX;
  }
  if (ratingData.rating) {
    const longTermRating = extractLongTermRatingValue(ratingData);
    return longTermRatingPassesFilter(longTermRating, minValue, maxValue);
  }
  return shortTermRatingPassesFilter(ratingData.shortTerm, minValue, maxValue);
}

function extractLongTermRatingValue(ratingData: EnhancedRating) {
  let ltRating = ratingData.rating;
  // Aaa2 = AAA, Aa1 = AA+, Baa3 = BBB-
  if (ratingData.agency === "Moody's") {
    ltRating = mapMoodyAgencyRating(ltRating);
  }

  ltRating = mapUnidentifiedRatingToNonIG(ltRating);

  return ltRating;
}

function mapMoodyAgencyRating(rating: string) {
  return rating
    .replace(/a/g, rating[0])
    .replace(/1/g, '+')
    .replace(/2/g, '')
    .replace(/3/g, '-');
}

function mapUnidentifiedRatingToNonIG(rating: string) {
  // Anything less than BBB- is Non-IG
  // Anything unidentified will be interpreted as Non-IG
  if (
    (rating[0] !== 'A' && rating[0] !== 'B') ||
    (rating[0] === 'B' && rating.length < 4 && rating !== 'BBB')
  ) {
    return 'Non-IG';
  }
  return rating;
}

function shortTermRatingPassesFilter(
  shortTermRating: string,
  minValue: number,
  maxValue: number
) {
  const shortTermMatches = ST_RATINGS.map((ratingArr) =>
    ratingArr.vals.includes(shortTermRating)
  );
  // Anything unidentified will be interpreted as Non-IG
  if (shortTermRating && !shortTermMatches.includes(true)) {
    shortTermMatches[NON_IG_BUCKET_INDEX] = true;
  }
  return range(minValue, maxValue + 1).some((val) => shortTermMatches[val]);
}

function longTermRatingPassesFilter(
  longTermRating: string,
  minValue: number,
  maxValue: number
) {
  const index = RATINGS.indexOf(longTermRating);

  return index >= minValue && index <= maxValue;
}
